import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { v4 as uuidv4 } from "uuid";
import { useUserAuth } from "../../context/userAuthContext";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    onSnapshot
} from "firebase/firestore";
import {
    MapPin,
    Phone,
    Mail,
    Globe,
    Clock,
    ChevronLeft,
    Calendar,
    Share2,
    Star,
    BookOpen,
    ArrowLeft,
    Info,
    AlertTriangle,
    ExternalLink
} from "lucide-react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

// Importar componentes de productos y carrito
import ProductsSection from "../Products/ProductsSection";
import CartSidebar from "../Products/CartSidebar";

const PlaceInfo = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useUserAuth();
    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [activeTab, setActiveTab] = useState("info");
    const [owner, setOwner] = useState(null);
    const [categories, setCategories] = useState([]);
    const [currentDay, setCurrentDay] = useState("");
    const [isOpenNow, setIsOpenNow] = useState(false);

    // Estados para el carrito
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isCartProcessing, setIsCartProcessing] = useState(false);
    const [cartId, setCartId] = useState(null);
    const [cart, setCart] = useState({ items: [], total: 0 });

    const mapContainerStyle = {
        width: "100%",
        height: "250px",
    };

    // Función para obtener o crear un ID de carrito para usuarios anónimos
    const getCartId = () => {
        // Si el usuario está autenticado, usar su ID
        if (user) {
            return user.uid;
        }

        // Si no, intentar obtener el ID del localStorage
        let storedCartId = localStorage.getItem("vine_cart_id");

        // Si no existe, crear uno nuevo
        if (!storedCartId) {
            storedCartId = `anonymous_${uuidv4()}`;
            localStorage.setItem("vine_cart_id", storedCartId);
        }

        return storedCartId;
    };

    // Inicializar el ID del carrito
    useEffect(() => {
        const identifier = getCartId();
        setCartId(identifier);

        // Suscribirse a cambios en el carrito
        if (identifier) {
            const cartRef = doc(db, "carts", identifier);
            const unsubscribe = onSnapshot(
                cartRef,
                (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const cartData = docSnapshot.data();
                        setCart({
                            items: cartData.items || [],
                            total: 0 // El total se calculará en el componente CartSidebar
                        });
                    } else {
                        setCart({ items: [], total: 0 });
                    }
                },
                (error) => {
                    console.error("Error listening to cart:", error);
                }
            );

            // Limpiar el listener cuando el componente se desmonte
            return () => unsubscribe();
        }
    }, [user]);

    useEffect(() => {
        // Set current day for checking open status
        const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
        setCurrentDay(days[today]);

        const fetchPlace = async () => {
            try {
                setLoading(true);

                // Query for place using slug
                const placesRef = collection(db, "places");
                const q = query(placesRef, where("slug", "==", slug));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    setError("El restaurante no fue encontrado");
                    setLoading(false);
                    return;
                }

                const placeData = {
                    id: querySnapshot.docs[0].id,
                    ...querySnapshot.docs[0].data()
                };

                setPlace(placeData);

                // Check if place is currently open
                if (placeData.openingHours && placeData.openingHours[days[today]]) {
                    const { open, close, closed } = placeData.openingHours[days[today]];

                    if (!closed) {
                        const now = new Date();
                        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                        setIsOpenNow(currentTime >= open && currentTime <= close);
                    } else {
                        setIsOpenNow(false);
                    }
                }

                // Fetch owner information
                if (placeData.ownerId) {
                    const ownerDoc = await getDoc(doc(db, "Users", placeData.ownerId));
                    if (ownerDoc.exists()) {
                        setOwner(ownerDoc.data());
                    }
                }

                // Fetch categories information
                if (placeData.categories && placeData.categories.length > 0) {
                    const categoriesList = [];

                    for (const categoryId of placeData.categories) {
                        try {
                            // First try vineCategories collection
                            const vineCategoryDoc = await getDoc(doc(db, "vineCategories", categoryId));

                            if (vineCategoryDoc.exists()) {
                                categoriesList.push({
                                    id: categoryId,
                                    ...vineCategoryDoc.data()
                                });
                            } else {
                                // Try regular categories collection as fallback
                                const categoryDoc = await getDoc(doc(db, "categories", categoryId));
                                if (categoryDoc.exists()) {
                                    categoriesList.push({
                                        id: categoryId,
                                        ...categoryDoc.data()
                                    });
                                }
                            }
                        } catch (error) {
                            console.error("Error fetching category:", error);
                        }
                    }

                    setCategories(categoriesList);
                }

                setLoading(false);
            } catch (error) {
                console.error("Error fetching place:", error);
                setError("Error al cargar la información del restaurante");
                setLoading(false);
            }
        };

        fetchPlace();
    }, [slug]);

    // Handle share button
    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: place?.name || "Restaurante en Vine",
                    text: `Visita ${place?.name} en Vine Business`,
                    url: window.location.href,
                });
            } catch (error) {
                console.log("Error sharing:", error);
            }
        } else {
            // Fallback for browsers that don't support the Web Share API
            navigator.clipboard.writeText(window.location.href)
                .then(() => {
                    alert("Enlace copiado al portapapeles");
                })
                .catch(err => {
                    console.error("No se pudo copiar el enlace:", err);
                });
        }
    };

    // Format time for display
    const formatTime = (timeString) => {
        if (!timeString) return "";

        try {
            const [hours, minutes] = timeString.split(":");
            return `${hours}:${minutes}`;
        } catch (error) {
            return timeString;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-16 pb-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 pt-16 pb-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{error}</h1>
                    <p className="text-gray-600 mb-6">Este restaurante no existe o ha sido eliminado.</p>
                    <button
                        onClick={() => navigate("/")}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    if (!place) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header with images */}
            <div className="relative">
                {/* Image gallery/slider */}
                <div className="relative h-64 sm:h-80 md:h-96 bg-gray-100">
                    {place.images && place.images.length > 0 ? (
                        <>
                            <img
                                src={place.images[activeImageIndex]}
                                alt={place.name}
                                className="w-full h-full object-cover"
                            />
                            {/* Navigation dots for images */}
                            {place.images.length > 1 && (
                                <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                                    {place.images.map((_, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`h-2 w-2 rounded-full ${activeImageIndex === index
                                                ? "bg-white"
                                                : "bg-white/50"
                                                }`}
                                            onClick={() => setActiveImageIndex(index)}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <Info className="h-12 w-12 text-gray-400" />
                        </div>
                    )}

                    {/* Back button */}
                    <button
                        onClick={() => navigate(-1)}
                        className="absolute top-4 left-4 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-all"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>

                    {/* Share button */}
                    <button
                        onClick={handleShare}
                        className="absolute top-4 right-4 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-all"
                    >
                        <Share2 className="h-5 w-5" />
                    </button>
                </div>

                {/* Open now indicator */}
                <div className="absolute bottom-0 transform translate-y-1/2 left-4 sm:left-8">
                    <div
                        className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium shadow ${isOpenNow
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                    >
                        {isOpenNow ? "Abierto ahora" : "Cerrado ahora"}
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Place info header */}
                    <div className="p-6 border-b border-gray-200">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{place.name}</h1>

                        {/* Categories */}
                        {categories.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {categories.map((category) => (
                                    <span
                                        key={category.id}
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                    >
                                        {category.iconUrl && (
                                            <img src={category.iconUrl} alt="" className="w-3 h-3 mr-1" />
                                        )}
                                        {category.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Address */}
                        {place.address && (
                            <div className="flex items-start text-gray-600 mb-2">
                                <MapPin className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                                <span>{place.address}</span>
                            </div>
                        )}

                        {/* Description */}
                        {place.description && (
                            <p className="text-gray-600 mt-4">{place.description}</p>
                        )}
                    </div>

                    {/* Tabs navigation */}
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            <button
                                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "info"
                                    ? "border-purple-500 text-purple-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                                onClick={() => setActiveTab("info")}
                            >
                                Información
                            </button>
                            <button
                                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "location"
                                    ? "border-purple-500 text-purple-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                                onClick={() => setActiveTab("location")}
                            >
                                Ubicación
                            </button>
                            <button
                                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === "hours"
                                    ? "border-purple-500 text-purple-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                                onClick={() => setActiveTab("hours")}
                            >
                                Horarios
                            </button>
                        </nav>
                    </div>

                    {/* Tab content */}
                    <div className="p-6">
                        {/* Info tab */}
                        {activeTab === "info" && (
                            <div className="space-y-6">
                                {/* Contact information */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Información de contacto</h3>
                                    <div className="space-y-3">
                                        {place.phone && (
                                            <div className="flex items-center">
                                                <Phone className="h-5 w-5 text-gray-400 mr-3" />
                                                <a
                                                    href={`tel:${place.phone}`}
                                                    className="text-purple-600 hover:text-purple-800"
                                                >
                                                    {place.phone}
                                                </a>
                                            </div>
                                        )}

                                        {place.email && (
                                            <div className="flex items-center">
                                                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                                                <a
                                                    href={`mailto:${place.email}`}
                                                    className="text-purple-600 hover:text-purple-800"
                                                >
                                                    {place.email}
                                                </a>
                                            </div>
                                        )}

                                        {place.website && (
                                            <div className="flex items-center">
                                                <Globe className="h-5 w-5 text-gray-400 mr-3" />
                                                <a
                                                    href={place.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-purple-600 hover:text-purple-800 flex items-center"
                                                >
                                                    {place.website.replace(/^https?:\/\//, '')}
                                                    <ExternalLink className="h-4 w-4 ml-1" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Owner information (if available) */}
                                {owner && (
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">Dueño del restaurante</h3>
                                        <div className="flex items-center">
                                            {owner.photoUrl ? (
                                                <img
                                                    src={owner.photoUrl}
                                                    alt={`${owner.nombre} ${owner.apellido}`}
                                                    className="h-10 w-10 rounded-full mr-3"
                                                />
                                            ) : (
                                                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                                                    <span className="text-purple-800 font-medium">
                                                        {owner.nombre ? owner.nombre.charAt(0) : "?"}
                                                    </span>
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium">
                                                    {owner.nombre} {owner.apellido}
                                                </p>
                                                {owner.nickname && (
                                                    <p className="text-sm text-gray-500">@{owner.nickname}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Location tab */}
                        {activeTab === "location" && (
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Ubicación</h3>
                                {place.location && (
                                    <>
                                        <div className="rounded-lg overflow-hidden mb-4">
                                            <LoadScript
                                                googleMapsApiKey="AIzaSyBSXE8zZ-SqMWdDz16hr00Djvkm3mMit28"
                                            >
                                                <GoogleMap
                                                    mapContainerStyle={mapContainerStyle}
                                                    center={place.location}
                                                    zoom={15}
                                                >
                                                    <Marker position={place.location} />
                                                </GoogleMap>
                                            </LoadScript>
                                        </div>

                                        <div className="flex items-start">
                                            <MapPin className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-gray-600">{place.address}</p>
                                                <a
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${place.location.lat},${place.location.lng}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-purple-600 hover:text-purple-800 text-sm flex items-center mt-2"
                                                >
                                                    Obtener indicaciones
                                                    <ExternalLink className="h-3 w-3 ml-1" />
                                                </a>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Hours tab */}
                        {activeTab === "hours" && (
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Horarios de atención</h3>

                                {place.openingHours && (
                                    <div className="space-y-2">
                                        {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                                            const dayLabel = {
                                                monday: "Lunes",
                                                tuesday: "Martes",
                                                wednesday: "Miércoles",
                                                thursday: "Jueves",
                                                friday: "Viernes",
                                                saturday: "Sábado",
                                                sunday: "Domingo",
                                            };

                                            const hours = place.openingHours[day];
                                            const isToday = day === currentDay;

                                            return (
                                                <div
                                                    key={day}
                                                    className={`flex justify-between py-2 px-3 rounded-lg ${isToday ? "bg-purple-50" : ""
                                                        }`}
                                                >
                                                    <div className="flex items-center">
                                                        {isToday && (
                                                            <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                                                        )}
                                                        <span className={`font-medium ${isToday ? "text-purple-700" : "text-gray-700"}`}>
                                                            {dayLabel[day]}
                                                        </span>
                                                    </div>

                                                    <div className="text-right">
                                                        {hours.closed ? (
                                                            <span className="text-red-600">Cerrado</span>
                                                        ) : (
                                                            <span>
                                                                {formatTime(hours.open)} - {formatTime(hours.close)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Products Section */}
                {place && cartId && (
                    <ProductsSection
                        placeId={place.id}
                        placeName={place.name}
                        placeSlug={slug}
                        cartId={cartId}
                        onCartOpen={() => setIsCartOpen(true)}
                    />
                )}

                {/* URL information */}
                <div className="mt-6 bg-white rounded-lg shadow-md p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Globe className="h-5 w-5 text-purple-500 mr-2" />
                            <span className="text-sm text-gray-600">vine.mx/go/{slug}</span>
                        </div>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(`vine.mx/go/${slug}`);
                                alert("URL copiada al portapapeles");
                            }}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                            Copiar URL
                        </button>
                    </div>
                </div>
            </div>

            {/* Cart Sidebar */}
            {place && cartId && (
                <CartSidebar
                    isOpen={isCartOpen}
                    onClose={() => setIsCartOpen(false)}
                    placeId={place.id}
                    cart={cart}
                    cartId={cartId}
                    isProcessing={isCartProcessing}
                    setIsProcessing={setIsCartProcessing}
                />
            )}
        </div>
    );
};

export default PlaceInfo;